import { DurationUnitType } from 'dayjs/plugin/duration'
import { dayjs } from './dayjs'
import { diacritic } from './diacritic'
export default defineNuxtPlugin(() => {
  const store = $store()
  const config = useRuntimeConfig().public
  const getTimeFormat = (type?: string): string => {
    const dateFormat = store.system.dateFormat
    const timeFormat = store.system.timeFormat
    const timezone = store.system.timezone

    if (type === 'dateFormat') {
      return dateFormat
    }

    if (type === 'timeFormat') {
      return timeFormat
    }

    if (type === 'dateTimeFormat') {
      return dateFormat + ' ' + timeFormat
    }

    if (type === 'timezone') {
      return timezone.toString()
    }

    return ''
  }
  const defaultDatetimeFormat = (type: string = 'dateTimeFormat') => getTimeFormat(type)

  const formatCurrency = (val: number, round = 2) => {
    if (isNaN(val) || typeof val === 'string') {
      return val
    }
    return Number(val).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: round
    })
  }

  const formatNumber = (number: number | string) => {
    try {
      const parts = number.toString().split('.')
      if (parts.length > 2) {
        return number.toString()
      }
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    } catch (e: any) {
      return number
    }
  }

  const date = (value: number | string | dayjs.Dayjs, format?: string): string => {
    if (!value) {
      return ''
    }
    if (!dayjs(value).isValid()) {
      return value.toString()
    }
    if (format === undefined) {
      format = defaultDatetimeFormat('dateFormat')
    }
    return dayjs(value).format(format)
  }

  const datetime = (value: number | string | dayjs.Dayjs, format?: string): string => {
    if (!value) {
      return ''
    }

    if (!dayjs(value).isValid()) {
      return value.toString()
    }

    try {
      if (format === undefined) {
        format = defaultDatetimeFormat()
      }

      const timezone = getTimeFormat('timezone')

      return dayjs(value).utcOffset(timezone).format(format)
    } catch (e) {
      return ''
    }
  }

  const datetimeUTC = (value: number | string, format?: string): string => {
    if (!value) {
      return ''
    }
    if (!dayjs(value).isValid()) {
      return value.toString()
    }
    if (format === undefined) {
      format = defaultDatetimeFormat()
    }
    try {
      return dayjs(value).utc().format(format)
    } catch (e) {
      return ''
    }
  }

  const dateTimeFromNow = (value: number | string): string => {
    if (!value) {
      return ''
    }
    try {
      const timezone = getTimeFormat('timezone')
      return dayjs(value).utcOffset(timezone).fromNow(true)
    } catch (e) {
      return ''
    }
  }

  const timeDifferent = (val: number | string): string => {
    return dayjs(val).fromNow()
  }

  const removeDiacritics = (value: string): string => {
    return diacritic.clean(value)
  }

  const timeToString = (value: number, unit: DurationUnitType = 'h') => {
    const time = dayjs.duration(value, unit)
    const totalHours = Math.floor(time.asHours()) // Lấy tổng số giờ, không cần chia thành ngày
    const minutes = time.minutes()

    return `${totalHours} giờ ${minutes} phút`
  }

  const duration = (value: number, unit: DurationUnitType = 'h') => {
    const time = dayjs.duration(value, unit)
    const seconds = time.seconds()
    const minutes = time.minutes()
    const hours = time.hours()
    const days = time.days()
    let result: string = ''
    if (days) {
      result += `${days} ngày`
    }
    if (hours) {
      result += ` ${hours} giờ`
    }
    if (minutes) {
      result += ` ${minutes} phút`
    }
    if (seconds) {
      result += ` ${seconds} giây`
    }
    return result
  }

  const convertToHHMM = (time: number, type: string = 'hour') => {
    if (typeof time !== 'number') {
      return ''
    }

    let hours: number
    let minutes: number

    if (type === 'hour') {
      hours = Math.floor(time)
      minutes = Math.floor((time - hours) * 60)
    } else if (type === 'minute') {
      minutes = Math.floor(time)
      hours = Math.floor(minutes / 60)
      minutes = minutes % 60
    } else if (type === 'second') {
      minutes = Math.floor(time / 60)
      hours = Math.floor(minutes / 60)
    } else {
      return ''
    }

    const formattedTime = (hours < 10 ? ('0' + hours).slice(-2) : hours) + ':' + (minutes < 10 ? ('0' + minutes).slice(-2) : minutes)

    return formattedTime
  }

  const convertMinutesToNaturalLanguage = (time: number, type: string = 'hour') => {
    if (typeof time !== 'number') {
      return 0
    }

    let hours: number
    let minutes: number

    if (type === 'hour') {
      hours = Math.floor(time)
      minutes = Math.floor((time - hours) * 60)
    } else if (type === 'minute') {
      minutes = Math.floor(time)
      hours = Math.floor(minutes / 60)
      minutes = minutes % 60
    } else if (type === 'second') {
      minutes = Math.floor(time / 60)
      hours = Math.floor(minutes / 60)
      minutes -= hours * 60
    } else {
      return ''
    }

    if (!hours && !minutes) {
      return '0 giờ'
    }

    return (hours ? `${hours} giờ ` : '') + (minutes ? `${minutes} phút` : '')
  }

  const cdnUrl = (source: string | { [key: string]: any }) => {
    if (!source) {
      return ''
    }
    if (typeof source === 'object') {
      const { path, fileOriginName } = source

      const cdn = config.cdnUrl || 'https://cdn-hts-dev.freightek.com/'
      return `${cdn}${path}?response-content-disposition=` + encodeURIComponent(`attachment;filename*=UTF-8''${encodeURIComponent(fileOriginName)}`)
    }
    if (source?.indexOf('/') === 0) {
      source = source.substring(1)
    }
    const cdn = config.cdnUrl || 'https://cdn-hts-dev.freightek.com/'
    return `${cdn}${source}`
  }

  const generateId = () => {
    return Math.random().toString(36).substr(2, 9)
  }

  // m to km
  const mToKm = (value: number, precision: number = 2) => {
    return $lodash.round(value / 1000, precision)
  }

  // giây => giờ
  const sToH = (value: number, precision: number = 2) => {
    return $lodash.round(value / 60 / 60, precision)
  }

  return {
    provide: {
      formatters: {
        defaultDatetimeFormat,
        formatCurrency,
        formatNumber,
        date,
        datetime,
        datetimeUTC,
        dateTimeFromNow,
        timeDifferent,
        removeDiacritics,
        timeToString,
        convertToHHMM,
        convertMinutesToNaturalLanguage,
        duration,
        cdnUrl,
        generateId,
        mToKm,
        sToH
      }
    }
  }
})
